import {Action, Actions, Avatar, Choice, FormControl} from 'platform/components';
import {VStack, Text, Box, HStack, Space} from 'platform/foundation';

import {useFieldArray} from 'react-hook-form';

import i18n from '@dms/i18n';

import {buildArray, Nullish} from 'shared/web';

import {avatarColors} from '../constants/kanbanColors';
import type {KanbanSettings} from '../types/KanbanSettings';
import type {KanbanUser} from '../types/KanbanUser';

type UserControlProps = {
  control: FormControl<KanbanSettings>;
  currentUserId: string;
  users: KanbanUser[] | Nullish;
};

export function UserControl(props: UserControlProps) {
  const fieldArray = useFieldArray({
    control: props.control,
    name: 'selectedUsers',
    keyName: 'formFieldId',
  });

  const onUserSelect = (userId: string | null) => {
    const user = props.users?.find((user) => user.id === userId);
    if (!user) {
      return;
    }
    fieldArray.append({...user, isHidden: false});
  };

  const userOptions = props.users
    ?.filter((user) => !fieldArray.fields.some((selectedUser) => selectedUser.id === user.id))
    ?.map((user) => ({
      label: `${user.fullName} <${user.email}>`,
      value: user.id,
    }));

  return (
    <VStack spacing={4}>
      <Choice
        value=""
        autoFocus
        options={userOptions ?? []}
        isSearchable
        isNotClearable
        isDisabled={fieldArray.fields.length === 6}
        placeholder={i18n.t('general.labels.searchPlaceholder')}
        onChange={onUserSelect}
      />
      <VStack spacing={2}>
        {fieldArray.fields.map((field, fieldIndex) => (
          <Box
            key={field.formFieldId}
            borderLeft="2px solid"
            borderColor={avatarColors[fieldIndex]}
            paddingHorizontal={2}
          >
            <HStack align="center" minHeight={9}>
              <Box opacity={field.isHidden ? 0.2 : 1}>
                <Avatar
                  name={field.fullName}
                  size="small"
                  color={avatarColors[fieldIndex]}
                  textColor="white"
                />
              </Box>
              <Space horizontal={2} />
              <Text size="small">{field.fullName}</Text>
              <Space fillAvailable />
              <Actions
                size="small"
                actions={buildArray<Action>()
                  .add({
                    type: 'iconButton',
                    isDisabled:
                      !field.isHidden &&
                      fieldArray.fields.filter((selectedUser) => !selectedUser.isHidden).length ===
                        1,
                    icon: field.isHidden ? 'action/visibility_off' : 'action/visibility',
                    onClick: () => {
                      fieldArray.update(fieldIndex, {...field, isHidden: !field.isHidden});
                    },
                  })
                  .when(field.id !== props.currentUserId, {
                    type: 'iconButton',
                    icon: 'navigation/close',
                    onClick: () => {
                      fieldArray.remove(fieldIndex);
                    },
                  })}
              />
            </HStack>
          </Box>
        ))}
      </VStack>
    </VStack>
  );
}
